import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, IconButton, Alert, Radio, RadioGroup, FormControlLabel, Autocomplete, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemButton, ListItemText, Chip, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ConnectingAirportsIcon from '@mui/icons-material/ConnectingAirports';
import { API_BASE_URL } from './config';
import { useTranslation } from 'react-i18next';
import airlineCodes from './data/airline_codes.json';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '90%', md: 600 },
    maxWidth: '90vw',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: { xs: 2, sm: 4 },
    maxHeight: '90vh',
    overflowY: 'auto'
};

const TransportationFormModal = ({ open, onClose, tripId, dayLabel, defaultDestination, defaultDepartureDate, tripDays, selectedDayNumber, existingRecord = null }) => {
    const { t } = useTranslation();
    const [transportType, setTransportType] = useState('Flight');
    const [flightType, setFlightType] = useState('round-trip');
    
    // Airline autocomplete - filter out defunct airlines and prepare options
    const airlineOptions = useMemo(() => {
        return airlineCodes
            .filter(a => a.code && a.name && !a.comments?.toLowerCase().includes('defunct'))
            .map(a => ({
                code: a.code,
                name: a.name,
                label: `${a.name} (${a.code})`,
                country: a.country || ''
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, []);
    
    // Smart airline filter function with prioritized matching
    const filterAirlineOptions = (options, inputValue) => {
        if (!inputValue) return options.slice(0, 20);
        
        const filterValue = inputValue.toLowerCase().trim();
        const len = filterValue.length;
        
        if (len <= 3) {
            // 1-3 chars: prioritize by airline code, then by name
            // 1. Airlines where code STARTS with the input (sorted by code)
            const codeStartsWith = options
                .filter(o => o.code.toLowerCase().startsWith(filterValue))
                .sort((a, b) => a.code.localeCompare(b.code));
            
            // 2. Airlines where code CONTAINS the input but doesn't start (sorted by code)
            const codeContains = options
                .filter(o => 
                    o.code.toLowerCase().includes(filterValue) && 
                    !o.code.toLowerCase().startsWith(filterValue)
                )
                .sort((a, b) => a.code.localeCompare(b.code));
            
            // 3. Airlines where NAME contains the input but code doesn't (sorted by name)
            const nameContains = options
                .filter(o => 
                    o.name.toLowerCase().includes(filterValue) &&
                    !o.code.toLowerCase().includes(filterValue)
                )
                .sort((a, b) => a.name.localeCompare(b.name));
            
            return [...codeStartsWith, ...codeContains, ...nameContains].slice(0, 25);
        } else {
            // 4+ chars: only match by airline name (sorted by name)
            return options
                .filter(o => o.name.toLowerCase().includes(filterValue))
                .sort((a, b) => a.name.localeCompare(b.name))
                .slice(0, 25);
        }
    };
    
    // Selected airline objects (for autocomplete value)
    const [selectedAirline, setSelectedAirline] = useState(null);
    const [selectedReturnAirline, setSelectedReturnAirline] = useState(null);
    const [fetchingFlight, setFetchingFlight] = useState(false);
    const [fetchingReturnFlight, setFetchingReturnFlight] = useState(false);
    
    // Day Selection for round trips
    const [returnDayNumber, setReturnDayNumber] = useState('');
    
    // Departure Flight
    const [airline, setAirline] = useState('');
    const [flightNumber, setFlightNumber] = useState('');
    const [flightConfirmation, setFlightConfirmation] = useState('');
    const [departureDate, setDepartureDate] = useState('');
    const [departureTime, setDepartureTime] = useState('');
    const [arrivalTime, setArrivalTime] = useState('');
    const [flightNotFound, setFlightNotFound] = useState(false);
    const [departureAirport, setDepartureAirport] = useState('');
    const [arrivalAirport, setArrivalAirport] = useState('');
    const [flightDataSource, setFlightDataSource] = useState(''); // 'live' or 'historical'

    // Return Flight
    const [returnAirline, setReturnAirline] = useState('');
    const [returnFlightNumber, setReturnFlightNumber] = useState('');
    const [returnDepartureDate, setReturnDepartureDate] = useState('');
    const [returnDepartureTime, setReturnDepartureTime] = useState('');
    const [returnArrivalTime, setReturnArrivalTime] = useState('');
    const [returnFlightNotFound, setReturnFlightNotFound] = useState(false);
    const [returnDepartureAirport, setReturnDepartureAirport] = useState('');
    const [returnArrivalAirport, setReturnArrivalAirport] = useState('');
    const [returnFlightDataSource, setReturnFlightDataSource] = useState(''); // 'live' or 'historical'

    // Flight search results dialog
    const [flightSearchResults, setFlightSearchResults] = useState([]);
    const [flightSearchDialogOpen, setFlightSearchDialogOpen] = useState(false);
    const [isSearchingReturnFlight, setIsSearchingReturnFlight] = useState(false);

    // Connecting flights state (for multi-leg journeys)
    const [connectingFlights, setConnectingFlights] = useState([]);
    const [fetchingConnectingFlight, setFetchingConnectingFlight] = useState({});
    const [connectingFlightNotFound, setConnectingFlightNotFound] = useState({});
    const [connectingFlightSearchIndex, setConnectingFlightSearchIndex] = useState(null);

    // Return connecting flights state (for multi-leg return journeys)
    const [returnConnectingFlights, setReturnConnectingFlights] = useState([]);
    const [fetchingReturnConnectingFlight, setFetchingReturnConnectingFlight] = useState({});
    const [returnConnectingFlightNotFound, setReturnConnectingFlightNotFound] = useState({});
    const [returnConnectingFlightSearchIndex, setReturnConnectingFlightSearchIndex] = useState(null);

    const [freeText, setFreeText] = useState('');
    const [destinationCity, setDestinationCity] = useState(defaultDestination || '');
    const [bookingReference, setBookingReference] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            // Reset form when modal opens
            setTransportType('Flight');
            setFlightType('round-trip');
            
            setAirline('');
            setSelectedAirline(null);
            setFlightNumber('');
            setFlightConfirmation('');
            setDepartureDate(defaultDepartureDate || '');
            setDepartureTime('');
            setArrivalTime('');
            setFlightNotFound(false);
            setDepartureAirport('');
            setArrivalAirport('');
            setFlightDataSource('');

            setReturnAirline('');
            setSelectedReturnAirline(null);
            setReturnFlightNumber('');
            setReturnDepartureDate('');
            setReturnDepartureTime('');
            setReturnArrivalTime('');
            setReturnFlightNotFound(false);
            setReturnDepartureAirport('');
            setReturnArrivalAirport('');
            setReturnFlightDataSource('');
            
            setFlightSearchResults([]);
            setFlightSearchDialogOpen(false);

            // Reset connecting flights
            setConnectingFlights([]);
            setFetchingConnectingFlight({});
            setConnectingFlightNotFound({});
            setConnectingFlightSearchIndex(null);

            // Reset return connecting flights
            setReturnConnectingFlights([]);
            setFetchingReturnConnectingFlight({});
            setReturnConnectingFlightNotFound({});
            setReturnConnectingFlightSearchIndex(null);

            // Auto-populate return day value (prefer ISO date key)
            if (tripDays && tripDays.length > 0) {
                const lastDay = tripDays[tripDays.length - 1];
                setReturnDayNumber(lastDay.date || lastDay.label || '');
            } else {
                setReturnDayNumber('');
            }

            setFreeText('');
            setDestinationCity(defaultDestination || '');
            setBookingReference('');
            setError('');

            // Edit mode: prefill from existing transportation record
            if (existingRecord) {
                const parseLegs = (value) => {
                    if (!value) return [];
                    if (Array.isArray(value)) return value;
                    if (typeof value === 'string') {
                        try {
                            const parsed = JSON.parse(value);
                            return Array.isArray(parsed) ? parsed : [];
                        } catch {
                            return [];
                        }
                    }
                    return [];
                };

                const transport = existingRecord.transport_type || 'Flight';
                setTransportType(transport);

                if (transport === 'Flight') {
                    setFlightType(existingRecord.flight_type || 'one-way');
                    setAirline(existingRecord.airline || '');
                    setFlightNumber(existingRecord.flight_number || '');
                    setFlightConfirmation(existingRecord.flight_confirmation || '');
                    setDepartureDate(existingRecord.departure_date || defaultDepartureDate || '');
                    setDepartureTime(existingRecord.departure_time || '');
                    setArrivalTime(existingRecord.arrival_time || '');
                    setDepartureAirport(existingRecord.departure_airport || '');
                    setArrivalAirport(existingRecord.arrival_airport || '');

                    const depLegs = parseLegs(existingRecord.connecting_flights).map((leg) => ({
                        ...leg,
                        selectedAirline: null,
                        airline: leg.airline || ''
                    }));
                    setConnectingFlights(depLegs);

                    setReturnAirline(existingRecord.return_airline || '');
                    setReturnFlightNumber(existingRecord.return_flight_number || '');
                    setReturnDepartureDate(existingRecord.return_departure_date || '');
                    setReturnDepartureTime(existingRecord.return_departure_time || '');
                    setReturnArrivalTime(existingRecord.return_arrival_time || '');
                    setReturnDepartureAirport(existingRecord.return_departure_airport || '');
                    setReturnArrivalAirport(existingRecord.return_arrival_airport || '');
                    setReturnDayNumber(existingRecord.return_departure_date || '');

                    const retLegs = parseLegs(existingRecord.return_connecting_flights).map((leg) => ({
                        ...leg,
                        selectedAirline: null,
                        airline: leg.airline || ''
                    }));
                    setReturnConnectingFlights(retLegs);

                    const matchedDep = airlineOptions.find(a => a.code === (existingRecord.airline || '')) || null;
                    const matchedRet = airlineOptions.find(a => a.code === (existingRecord.return_airline || '')) || null;
                    setSelectedAirline(matchedDep);
                    setSelectedReturnAirline(matchedRet);
                } else {
                    setFreeText(existingRecord.free_text || '');
                    setDestinationCity(existingRecord.destination_city || defaultDestination || '');
                    setBookingReference(existingRecord.booking_reference || '');
                }
            }
        }
    }, [open, defaultDestination, defaultDepartureDate, tripDays, existingRecord, airlineOptions]);

    // Auto-populate return date when switching to round-trip
    useEffect(() => {
        if (flightType === 'round-trip' && departureDate && !returnDepartureDate) {
            // Set return date to 3-5 days after departure, or use trip's last day if available
            if (tripDays && tripDays.length > 0) {
                // Find the last day's date from tripDays
                const lastDay = tripDays[tripDays.length - 1];
                if (lastDay.date) {
                    setReturnDepartureDate(lastDay.date);
                }
            } else {
                // Fallback: add 3 days to departure date
                const depDate = new Date(departureDate);
                depDate.setDate(depDate.getDate() + 3);
                const returnDate = depDate.toISOString().split('T')[0];
                setReturnDepartureDate(returnDate);
            }
        }
    }, [flightType]);

    // Auto-fetch flight info when airline code and flight number are both filled
    useEffect(() => {
        const autoFetchDepartureInfo = async () => {
            if (selectedAirline?.code && flightNumber && departureDate && !departureTime && !fetchingFlight) {
                setFetchingFlight(true);
                setFlightNotFound(false);
                try {
                    const response = await fetch(`${API_BASE_URL}/get_flight_details.php?carrierCode=${selectedAirline.code}&flightNumber=${flightNumber}&scheduledDepartureDate=${departureDate}&allowScraping=1`);
                    if (response.ok) {
                        const data = await response.json();
                        setDepartureTime(data.departureTime || '');
                        setArrivalTime(data.arrivalTime || '');
                        setDepartureAirport(data.departureAirport || '');
                        setArrivalAirport(data.arrivalAirport || '');
                        // Set data source - 'live' for Amadeus, 'historical' for DB fallback
                        setFlightDataSource(data.isHistorical ? 'historical' : 'live');
                    } else {
                        setFlightNotFound(true);
                        setFlightDataSource('');
                    }
                } catch (err) {
                    console.error('Auto-fetch flight error:', err);
                } finally {
                    setFetchingFlight(false);
                }
            }
        };
        
        // Debounce auto-fetch
        const timeoutId = setTimeout(autoFetchDepartureInfo, 500);
        return () => clearTimeout(timeoutId);
    }, [selectedAirline, flightNumber, departureDate]);

    // Auto-fetch return flight info
    useEffect(() => {
        const autoFetchReturnInfo = async () => {
            const returnCode = selectedReturnAirline?.code || selectedAirline?.code;
            if (returnCode && returnFlightNumber && returnDepartureDate && !returnDepartureTime && !fetchingReturnFlight) {
                setFetchingReturnFlight(true);
                setReturnFlightNotFound(false);
                try {
                    const response = await fetch(`${API_BASE_URL}/get_flight_details.php?carrierCode=${returnCode}&flightNumber=${returnFlightNumber}&scheduledDepartureDate=${returnDepartureDate}&allowScraping=1`);
                    if (response.ok) {
                        const data = await response.json();
                        setReturnDepartureTime(data.departureTime || '');
                        setReturnArrivalTime(data.arrivalTime || '');
                        setReturnDepartureAirport(data.departureAirport || '');
                        setReturnArrivalAirport(data.arrivalAirport || '');
                        // Set data source for return flight
                        setReturnFlightDataSource(data.isHistorical ? 'historical' : 'live');
                    } else {
                        setReturnFlightNotFound(true);
                        setReturnFlightDataSource('');
                    }
                } catch (err) {
                    console.error('Auto-fetch return flight error:', err);
                } finally {
                    setFetchingReturnFlight(false);
                }
            }
        };
        
        const timeoutId = setTimeout(autoFetchReturnInfo, 500);
        return () => clearTimeout(timeoutId);
    }, [selectedReturnAirline, selectedAirline, returnFlightNumber, returnDepartureDate]);

    const handleFetchFlightInfo = async (isReturn = false) => {
        setLoading(true);
        setError('');
        
        // Use the airline code from selected airline object, falling back to text input
        const currentAirlineCode = isReturn 
            ? (selectedReturnAirline?.code || selectedAirline?.code || returnAirline)
            : (selectedAirline?.code || airline);
        const currentFlightNumber = isReturn ? returnFlightNumber : flightNumber;
        const currentDate = isReturn ? returnDepartureDate : departureDate;

        if (isReturn) setReturnFlightNotFound(false);
        else setFlightNotFound(false);

        try {
            const response = await fetch(`${API_BASE_URL}/get_flight_details.php?carrierCode=${encodeURIComponent(currentAirlineCode)}&flightNumber=${encodeURIComponent(currentFlightNumber)}&scheduledDepartureDate=${currentDate}&allowScraping=1`);

            if (response.ok) {
                const data = await response.json();
                
                // Check if we have multiple results
                if (data.data && data.data.length > 1) {
                    setFlightSearchResults(data.data);
                    setIsSearchingReturnFlight(isReturn);
                    setFlightSearchDialogOpen(true);
                    // Set data source for multiple results
                    if (isReturn) {
                        setReturnFlightDataSource(data.isHistorical ? 'historical' : 'live');
                    } else {
                        setFlightDataSource(data.isHistorical ? 'historical' : 'live');
                    }
                } else {
                    // Single result - apply directly
                    if (isReturn) {
                        setReturnDepartureTime(data.departureTime);
                        setReturnArrivalTime(data.arrivalTime);
                        setReturnDepartureAirport(data.departureAirport || '');
                        setReturnArrivalAirport(data.arrivalAirport || '');
                        setReturnFlightDataSource(data.isHistorical ? 'historical' : 'live');
                    } else {
                        setDepartureTime(data.departureTime);
                        setArrivalTime(data.arrivalTime);
                        setDepartureAirport(data.departureAirport || '');
                        setArrivalAirport(data.arrivalAirport || '');
                        setFlightDataSource(data.isHistorical ? 'historical' : 'live');
                    }
                }
            } else {
                if (isReturn) {
                    setReturnFlightNotFound(true);
                    setReturnDepartureTime('');
                    setReturnArrivalTime('');
                    setReturnDepartureAirport('');
                    setReturnArrivalAirport('');
                    setReturnFlightDataSource('');
                } else {
                    setFlightNotFound(true);
                    setDepartureTime('');
                    setArrivalTime('');
                    setDepartureAirport('');
                    setArrivalAirport('');
                    setFlightDataSource('');
                }
            }
        } catch (err) {
            setError(t('unexpected_error'));
        } finally {
            setLoading(false);
        }
    };

    // Handle flight selection from search results dialog
    const handleSelectFlight = (flight) => {
        let depTime = '';
        let arrTime = '';
        let depAirport = '';
        let arrAirport = '';
        
        if (flight.departure?.at) {
            const depDateTime = flight.departure.at;
            depTime = depDateTime.includes('T') ? depDateTime.split('T')[1].substring(0, 5) : depDateTime;
            depAirport = flight.departure.iataCode || '';
        }
        if (flight.arrival?.at) {
            const arrDateTime = flight.arrival.at;
            arrTime = arrDateTime.includes('T') ? arrDateTime.split('T')[1].substring(0, 5) : arrDateTime;
            arrAirport = flight.arrival.iataCode || '';
        }
        
        if (isSearchingReturnFlight) {
            setReturnDepartureTime(depTime);
            setReturnArrivalTime(arrTime);
            setReturnDepartureAirport(depAirport);
            setReturnArrivalAirport(arrAirport);
        } else {
            setDepartureTime(depTime);
            setArrivalTime(arrTime);
            setDepartureAirport(depAirport);
            setArrivalAirport(arrAirport);
        }
        
        setFlightSearchDialogOpen(false);
        setFlightSearchResults([]);
    };

    // Add a new connecting flight leg
    const addConnectingFlight = () => {
        const lastLeg = connectingFlights.length > 0 
            ? connectingFlights[connectingFlights.length - 1] 
            : null;
        const previousArrivalAirport = lastLeg ? lastLeg.arrivalAirport : arrivalAirport;
        
        setConnectingFlights([...connectingFlights, {
            id: Date.now(),
            selectedAirline: selectedAirline, // Default to same airline
            airline: airline,
            flightNumber: '',
            departureAirport: previousArrivalAirport, // Auto-fill from previous leg's arrival
            arrivalAirport: '',
            departureDate: departureDate, // Same date by default
            departureTime: '',
            arrivalTime: ''
        }]);
    };

    // Remove a connecting flight leg
    const removeConnectingFlight = (index) => {
        const updated = connectingFlights.filter((_, i) => i !== index);
        setConnectingFlights(updated);
        // Clean up fetching state
        const newFetching = { ...fetchingConnectingFlight };
        delete newFetching[index];
        setFetchingConnectingFlight(newFetching);
        const newNotFound = { ...connectingFlightNotFound };
        delete newNotFound[index];
        setConnectingFlightNotFound(newNotFound);
    };

    // Update a connecting flight field
    const updateConnectingFlight = (index, field, value) => {
        const updated = [...connectingFlights];
        updated[index] = { ...updated[index], [field]: value };
        
        // If updating arrivalAirport and there's a next leg, update its departureAirport
        if (field === 'arrivalAirport' && index < connectingFlights.length - 1) {
            updated[index + 1] = { ...updated[index + 1], departureAirport: value };
        }
        
        setConnectingFlights(updated);
    };

    // Auto-fetch connecting flight info when airline, flight number, and date are all provided
    useEffect(() => {
        if (connectingFlights.length === 0) return;
        const timeouts = [];
        connectingFlights.forEach((leg, index) => {
            const airlineCode = leg.selectedAirline?.code || leg.airline;
            if (airlineCode && leg.flightNumber && leg.departureDate && !leg.departureTime && !fetchingConnectingFlight[index]) {
                const timeout = setTimeout(async () => {
                    setFetchingConnectingFlight(prev => ({ ...prev, [index]: true }));
                    setConnectingFlightNotFound(prev => ({ ...prev, [index]: false }));
                    try {
                        const res = await fetch(`${API_BASE_URL}/get_flight_details.php?carrierCode=${encodeURIComponent(airlineCode)}&flightNumber=${encodeURIComponent(leg.flightNumber)}&scheduledDepartureDate=${leg.departureDate}&allowScraping=1`);
                        if (res.ok) {
                            const data = await res.json();
                            if (data.data && data.data.length > 1) {
                                setFlightSearchResults(data.data);
                                setConnectingFlightSearchIndex(index);
                                setIsSearchingReturnFlight(false);
                                setFlightSearchDialogOpen(true);
                            } else {
                                setConnectingFlights(prev => {
                                    const updated = [...prev];
                                    if (updated[index]) {
                                        updated[index] = {
                                            ...updated[index],
                                            departureTime: data.departureTime || '',
                                            arrivalTime: data.arrivalTime || '',
                                            departureAirport: data.departureAirport || updated[index].departureAirport,
                                            arrivalAirport: data.arrivalAirport || ''
                                        };
                                        if (data.arrivalAirport && index < updated.length - 1) {
                                            updated[index + 1] = { ...updated[index + 1], departureAirport: data.arrivalAirport };
                                        }
                                    }
                                    return updated;
                                });
                            }
                        } else {
                            setConnectingFlightNotFound(prev => ({ ...prev, [index]: true }));
                        }
                    } catch (err) {
                        console.error('Auto-fetch connecting flight error:', err);
                    } finally {
                        setFetchingConnectingFlight(prev => ({ ...prev, [index]: false }));
                    }
                }, 800);
                timeouts.push(timeout);
            }
        });
        return () => timeouts.forEach(id => clearTimeout(id));
    // eslint-disable-next-line
    }, [connectingFlights.map(l => `${l.selectedAirline?.code || l.airline}::${l.flightNumber}::${l.departureDate}::${l.departureTime}`).join('|')]);

    // Auto-fetch return connecting flight info when airline, flight number, and date are all provided
    useEffect(() => {
        if (returnConnectingFlights.length === 0) return;
        const timeouts = [];
        returnConnectingFlights.forEach((leg, index) => {
            const airlineCode = leg.selectedAirline?.code || leg.airline;
            if (airlineCode && leg.flightNumber && leg.departureDate && !leg.departureTime && !fetchingReturnConnectingFlight[index]) {
                const timeout = setTimeout(async () => {
                    setFetchingReturnConnectingFlight(prev => ({ ...prev, [index]: true }));
                    setReturnConnectingFlightNotFound(prev => ({ ...prev, [index]: false }));
                    try {
                        const res = await fetch(`${API_BASE_URL}/get_flight_details.php?carrierCode=${encodeURIComponent(airlineCode)}&flightNumber=${encodeURIComponent(leg.flightNumber)}&scheduledDepartureDate=${leg.departureDate}&allowScraping=1`);
                        if (res.ok) {
                            const data = await res.json();
                            if (data.data && data.data.length > 1) {
                                setFlightSearchResults(data.data);
                                setReturnConnectingFlightSearchIndex(index);
                                setIsSearchingReturnFlight(true);
                                setFlightSearchDialogOpen(true);
                            } else {
                                setReturnConnectingFlights(prev => {
                                    const updated = [...prev];
                                    if (updated[index]) {
                                        updated[index] = {
                                            ...updated[index],
                                            departureTime: data.departureTime || '',
                                            arrivalTime: data.arrivalTime || '',
                                            departureAirport: data.departureAirport || updated[index].departureAirport,
                                            arrivalAirport: data.arrivalAirport || ''
                                        };
                                        if (data.arrivalAirport && index < updated.length - 1) {
                                            updated[index + 1] = { ...updated[index + 1], departureAirport: data.arrivalAirport };
                                        }
                                    }
                                    return updated;
                                });
                            }
                        } else {
                            setReturnConnectingFlightNotFound(prev => ({ ...prev, [index]: true }));
                        }
                    } catch (err) {
                        console.error('Auto-fetch return connecting flight error:', err);
                    } finally {
                        setFetchingReturnConnectingFlight(prev => ({ ...prev, [index]: false }));
                    }
                }, 800);
                timeouts.push(timeout);
            }
        });
        return () => timeouts.forEach(id => clearTimeout(id));
    // eslint-disable-next-line
    }, [returnConnectingFlights.map(l => `${l.selectedAirline?.code || l.airline}::${l.flightNumber}::${l.departureDate}::${l.departureTime}`).join('|')]);

    // Fetch flight info for connecting flight
    const handleFetchConnectingFlightInfo = async (index) => {
        const leg = connectingFlights[index];
        const airlineCode = leg.selectedAirline?.code || leg.airline;
        
        if (!airlineCode || !leg.flightNumber || !leg.departureDate) return;
        
        setFetchingConnectingFlight(prev => ({ ...prev, [index]: true }));
        setConnectingFlightNotFound(prev => ({ ...prev, [index]: false }));
        
        try {
            const response = await fetch(`${API_BASE_URL}/get_flight_details.php?carrierCode=${encodeURIComponent(airlineCode)}&flightNumber=${encodeURIComponent(leg.flightNumber)}&scheduledDepartureDate=${leg.departureDate}&allowScraping=1`);
            
            if (response.ok) {
                const data = await response.json();
                
                // Check if we have multiple results
                if (data.data && data.data.length > 1) {
                    setFlightSearchResults(data.data);
                    setConnectingFlightSearchIndex(index);
                    setIsSearchingReturnFlight(false);
                    setFlightSearchDialogOpen(true);
                } else {
                    updateConnectingFlight(index, 'departureTime', data.departureTime || '');
                    updateConnectingFlight(index, 'arrivalTime', data.arrivalTime || '');
                    updateConnectingFlight(index, 'departureAirport', data.departureAirport || leg.departureAirport);
                    updateConnectingFlight(index, 'arrivalAirport', data.arrivalAirport || '');
                }
            } else {
                setConnectingFlightNotFound(prev => ({ ...prev, [index]: true }));
            }
        } catch (err) {
            console.error('Error fetching connecting flight:', err);
            setConnectingFlightNotFound(prev => ({ ...prev, [index]: true }));
        } finally {
            setFetchingConnectingFlight(prev => ({ ...prev, [index]: false }));
        }
    };

    // Add a new return connecting flight leg
    const addReturnConnectingFlight = () => {
        const lastLeg = returnConnectingFlights.length > 0 
            ? returnConnectingFlights[returnConnectingFlights.length - 1] 
            : null;
        const previousArrivalAirport = lastLeg ? lastLeg.arrivalAirport : returnArrivalAirport;
        
        setReturnConnectingFlights([...returnConnectingFlights, {
            id: Date.now(),
            selectedAirline: selectedReturnAirline || selectedAirline, // Default to return airline or departure airline
            airline: returnAirline || airline,
            flightNumber: '',
            departureAirport: previousArrivalAirport, // Auto-fill from previous leg's arrival
            arrivalAirport: '',
            departureDate: returnDepartureDate, // Same date by default
            departureTime: '',
            arrivalTime: ''
        }]);
    };

    // Remove a return connecting flight leg
    const removeReturnConnectingFlight = (index) => {
        const updated = returnConnectingFlights.filter((_, i) => i !== index);
        setReturnConnectingFlights(updated);
        // Clean up fetching state
        const newFetching = { ...fetchingReturnConnectingFlight };
        delete newFetching[index];
        setFetchingReturnConnectingFlight(newFetching);
        const newNotFound = { ...returnConnectingFlightNotFound };
        delete newNotFound[index];
        setReturnConnectingFlightNotFound(newNotFound);
    };

    // Update a return connecting flight field
    const updateReturnConnectingFlight = (index, field, value) => {
        const updated = [...returnConnectingFlights];
        updated[index] = { ...updated[index], [field]: value };
        
        // If updating arrivalAirport and there's a next leg, update its departureAirport
        if (field === 'arrivalAirport' && index < returnConnectingFlights.length - 1) {
            updated[index + 1] = { ...updated[index + 1], departureAirport: value };
        }
        
        setReturnConnectingFlights(updated);
    };

    // Fetch flight info for return connecting flight
    const handleFetchReturnConnectingFlightInfo = async (index) => {
        const leg = returnConnectingFlights[index];
        const airlineCode = leg.selectedAirline?.code || leg.airline;
        
        if (!airlineCode || !leg.flightNumber || !leg.departureDate) return;
        
        setFetchingReturnConnectingFlight(prev => ({ ...prev, [index]: true }));
        setReturnConnectingFlightNotFound(prev => ({ ...prev, [index]: false }));
        
        try {
            const response = await fetch(`${API_BASE_URL}/get_flight_details.php?carrierCode=${encodeURIComponent(airlineCode)}&flightNumber=${encodeURIComponent(leg.flightNumber)}&scheduledDepartureDate=${leg.departureDate}&allowScraping=1`);
            
            if (response.ok) {
                const data = await response.json();
                
                // Check if we have multiple results
                if (data.data && data.data.length > 1) {
                    setFlightSearchResults(data.data);
                    setReturnConnectingFlightSearchIndex(index);
                    setIsSearchingReturnFlight(true);
                    setFlightSearchDialogOpen(true);
                } else {
                    updateReturnConnectingFlight(index, 'departureTime', data.departureTime || '');
                    updateReturnConnectingFlight(index, 'arrivalTime', data.arrivalTime || '');
                    updateReturnConnectingFlight(index, 'departureAirport', data.departureAirport || leg.departureAirport);
                    updateReturnConnectingFlight(index, 'arrivalAirport', data.arrivalAirport || '');
                }
            } else {
                setReturnConnectingFlightNotFound(prev => ({ ...prev, [index]: true }));
            }
        } catch (err) {
            console.error('Error fetching return connecting flight:', err);
            setReturnConnectingFlightNotFound(prev => ({ ...prev, [index]: true }));
        } finally {
            setFetchingReturnConnectingFlight(prev => ({ ...prev, [index]: false }));
        }
    };

    // Handle flight selection from search results dialog - extended for connecting flights
    const handleSelectFlightExtended = (flight) => {
        let depTime = '';
        let arrTime = '';
        let depAirport = '';
        let arrAirport = '';
        
        if (flight.departure?.at) {
            const depDateTime = flight.departure.at;
            depTime = depDateTime.includes('T') ? depDateTime.split('T')[1].substring(0, 5) : depDateTime;
            depAirport = flight.departure.iataCode || '';
        }
        if (flight.arrival?.at) {
            const arrDateTime = flight.arrival.at;
            arrTime = arrDateTime.includes('T') ? arrDateTime.split('T')[1].substring(0, 5) : arrDateTime;
            arrAirport = flight.arrival.iataCode || '';
        }
        
        if (connectingFlightSearchIndex !== null) {
            // Updating a connecting flight
            const updated = [...connectingFlights];
            updated[connectingFlightSearchIndex] = {
                ...updated[connectingFlightSearchIndex],
                departureTime: depTime,
                arrivalTime: arrTime,
                departureAirport: depAirport,
                arrivalAirport: arrAirport
            };
            // If there's a next leg, update its departure airport
            if (connectingFlightSearchIndex < connectingFlights.length - 1) {
                updated[connectingFlightSearchIndex + 1] = {
                    ...updated[connectingFlightSearchIndex + 1],
                    departureAirport: arrAirport
                };
            }
            setConnectingFlights(updated);
            setConnectingFlightSearchIndex(null);
        } else if (returnConnectingFlightSearchIndex !== null) {
            // Updating a return connecting flight
            const updated = [...returnConnectingFlights];
            updated[returnConnectingFlightSearchIndex] = {
                ...updated[returnConnectingFlightSearchIndex],
                departureTime: depTime,
                arrivalTime: arrTime,
                departureAirport: depAirport,
                arrivalAirport: arrAirport
            };
            // If there's a next leg, update its departure airport
            if (returnConnectingFlightSearchIndex < returnConnectingFlights.length - 1) {
                updated[returnConnectingFlightSearchIndex + 1] = {
                    ...updated[returnConnectingFlightSearchIndex + 1],
                    departureAirport: arrAirport
                };
            }
            setReturnConnectingFlights(updated);
            setReturnConnectingFlightSearchIndex(null);
        } else if (isSearchingReturnFlight) {
            setReturnDepartureTime(depTime);
            setReturnArrivalTime(arrTime);
            setReturnDepartureAirport(depAirport);
            setReturnArrivalAirport(arrAirport);
        } else {
            setDepartureTime(depTime);
            setArrivalTime(arrTime);
            setDepartureAirport(depAirport);
            setArrivalAirport(arrAirport);
        }
        
        setFlightSearchDialogOpen(false);
        setFlightSearchResults([]);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');

        // Validate that return day is selected for round trips
        if (transportType === 'Flight' && flightType === 'round-trip' && !returnDayNumber) {
            setError(t('return_day_required_error') || 'Please select a return day for round trip flights');
            setLoading(false);
            return;
        }

        const transportationData = {
            tripId,
            dayLabel,
            transportType,
            returnDayLabel: (transportType === 'Flight' && flightType === 'round-trip') ? returnDayNumber : null,
        };

        if (transportType === 'Flight') {
            transportationData.flightDetails = {
                flightType,
                airline,
                flightNumber,
                flightConfirmation,
                departureDate,
                departureTime,
                arrivalTime,
                departureAirport,
                arrivalAirport,
            };
            
            // Add connecting flights if any
            if (connectingFlights.length > 0) {
                transportationData.flightDetails.connectingFlights = connectingFlights.map(leg => ({
                    airline: leg.selectedAirline?.code || leg.airline,
                    flightNumber: leg.flightNumber,
                    departureAirport: leg.departureAirport,
                    arrivalAirport: leg.arrivalAirport,
                    departureDate: leg.departureDate,
                    departureTime: leg.departureTime,
                    arrivalTime: leg.arrivalTime
                }));
            }
            
            if (flightType === 'round-trip') {
                transportationData.returnFlightDetails = {
                    airline: returnAirline,
                    flightNumber: returnFlightNumber,
                    departureDate: returnDepartureDate,
                    departureTime: returnDepartureTime,
                    arrivalTime: returnArrivalTime,
                    departureAirport: returnDepartureAirport,
                    arrivalAirport: returnArrivalAirport,
                };
                
                // Add return connecting flights if any
                if (returnConnectingFlights.length > 0) {
                    transportationData.returnFlightDetails.connectingFlights = returnConnectingFlights.map(leg => ({
                        airline: leg.selectedAirline?.code || leg.airline,
                        flightNumber: leg.flightNumber,
                        departureAirport: leg.departureAirport,
                        arrivalAirport: leg.arrivalAirport,
                        departureDate: leg.departureDate,
                        departureTime: leg.departureTime,
                        arrivalTime: leg.arrivalTime
                    }));
                }
            }
        } else {
            transportationData.otherDetails = {
                freeText,
                destinationCity,
                bookingReference,
            };
        }

        try {
            const token = localStorage.getItem('token');
            const method = existingRecord?.id ? 'PUT' : 'POST';
            if (existingRecord?.id) {
                transportationData.id = existingRecord.id;
            }

            const response = await fetch(`${API_BASE_URL}/transportation.php`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(transportationData)
            });

            const data = await response.json();

            if (response.ok) {
                onClose(); // Close modal on success
            } else {
                console.error('Failed to save transportation details:', data);
                setError(data.message || t('save_transportation_error'));
            }
        } catch (err) {
            console.error('Error saving transportation details:', err);
            setError(t('unexpected_error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="transportation-form-modal-title"
            aria-describedby="transportation-form-modal-description"
        >
            <Box sx={style}>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Typography id="transportation-form-modal-title" variant="h6" component="h2" gutterBottom>
                    {t('update_transportation_title', { day: dayLabel })}
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="transport-type-select-label">{t('transportation_type_full_label')}</InputLabel>
                    <Select
                        labelId="transport-type-select-label"
                        id="transport-type-select"
                        value={transportType}
                        label={t('transportation_type_full_label')}
                        onChange={(e) => setTransportType(e.target.value)}
                    >
                        <MenuItem value="Flight">{t('transportation_flight')}</MenuItem>
                        <MenuItem value="Other">{t('transportation_other_options')}</MenuItem>
                    </Select>
                </FormControl>

                {transportType === 'Flight' ? (
                    <>
                        <FormControl component="fieldset" margin="normal">
                            <RadioGroup row name="flightType" value={flightType} onChange={(e) => setFlightType(e.target.value)}>
                                <FormControlLabel value="one-way" control={<Radio />} label={t('flight_type_one_way')} />
                                <FormControlLabel value="round-trip" control={<Radio />} label={t('flight_type_round_trip')} />
                            </RadioGroup>
                        </FormControl>

                        <TextField
                            fullWidth
                            label={t('confirmation_number_label')}
                            value={flightConfirmation}
                            onChange={(e) => setFlightConfirmation(e.target.value)}
                            margin="normal"
                            size="small"
                            placeholder={t('confirmation_number_placeholder') || 'e.g., ABC123'}
                        />

                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>{t('departure_flight_title')}</Typography>
                            <Autocomplete
                                options={airlineOptions}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                value={selectedAirline}
                                onChange={(event, newValue) => {
                                    setSelectedAirline(newValue);
                                    setAirline(newValue?.code || '');
                                }}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        setAirline(newInputValue);
                                    }
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('airline_label')}
                                        margin="normal"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {fetchingFlight ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                filterOptions={(options, { inputValue }) => filterAirlineOptions(options, inputValue)}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.code}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body2">{option.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.code} {option.country && `• ${option.country}`}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                isOptionEqualToValue={(option, value) => option.code === value?.code}
                            />
                            <TextField
                                fullWidth
                                label={t('flight_number_label')}
                                value={flightNumber}
                                onChange={(e) => setFlightNumber(e.target.value)}
                                margin="normal"
                                size="small"
                                placeholder={t('flight_number_placeholder') || 'e.g., 1234'}
                                helperText={selectedAirline && flightNumber ? `${selectedAirline.code}${flightNumber}` : ''}
                            />
                            <TextField
                                fullWidth
                                label={t('departure_date_label')}
                                type="date"
                                value={departureDate}
                                onChange={(e) => setDepartureDate(e.target.value)}
                                margin="normal"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                            <Button 
                                onClick={() => handleFetchFlightInfo(false)} 
                                disabled={loading || !departureDate || !flightNumber || !/^\d+$/.test(flightNumber) || !(selectedAirline?.code || airline)} 
                                size="small" 
                                variant="outlined" 
                                sx={{ mt: 1 }}
                                title={!departureDate ? 'Date required' : (!flightNumber ? 'Flight number required' : (!/^\d+$/.test(flightNumber) ? 'Enter numeric flight number' : (!(selectedAirline?.code || airline) ? 'Airline required' : '')))}
                            >
                                {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                {t('fetch_flight_info_btn')}
                            </Button>
                            {flightNotFound && <Alert severity="warning" sx={{ mt: 1 }}>{t('flight_not_found_error')}</Alert>}
                            
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-end' }}>
                                <TextField
                                    fullWidth
                                    label={t('departure_airport_label') || 'From (IATA)'}
                                    value={departureAirport}
                                    onChange={(e) => setDepartureAirport(e.target.value.toUpperCase())}
                                    margin="normal"
                                    size="small"
                                    inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 2 } }}
                                    InputLabelProps={{ shrink: true }}
                                    placeholder="e.g. ATH"
                                />
                                <Typography sx={{ mb: 1.5, color: 'text.secondary', fontSize: '1.2rem', flexShrink: 0 }}>→</Typography>
                                <TextField
                                    fullWidth
                                    label={t('arrival_airport_label') || 'To (IATA)'}
                                    value={arrivalAirport}
                                    onChange={(e) => setArrivalAirport(e.target.value.toUpperCase())}
                                    margin="normal"
                                    size="small"
                                    inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 2 } }}
                                    InputLabelProps={{ shrink: true }}
                                    placeholder="e.g. BKK"
                                />
                                {flightDataSource && (
                                    <Chip 
                                        size="small" 
                                        label={flightDataSource === 'live' ? t('live_data') || 'Live' : t('historical_data') || 'Est.'}
                                        color={flightDataSource === 'live' ? 'success' : 'warning'}
                                        sx={{ mb: 1, flexShrink: 0, height: 20, fontSize: '0.7rem' }}
                                    />
                                )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <TextField
                                    fullWidth
                                    label={t('departure_time_label')}
                                    type="time"
                                    value={departureTime}
                                    onChange={(e) => setDepartureTime(e.target.value)}
                                    margin="normal"
                                    size="small"
                                    disabled={!flightNotFound}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    fullWidth
                                    label={t('arrival_time_label')}
                                    type="time"
                                    value={arrivalTime}
                                    onChange={(e) => setArrivalTime(e.target.value)}
                                    margin="normal"
                                    size="small"
                                    disabled={!flightNotFound}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                        </Box>

                        {/* Connecting Flights Section */}
                        {connectingFlights.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <ConnectingAirportsIcon color="primary" />
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {t('connecting_flights_title') || 'Connecting Flights'}
                                    </Typography>
                                </Box>
                                {connectingFlights.map((leg, index) => (
                                    <Box key={leg.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t('leg_label') || 'Leg'} {index + 2} {/* +2 because leg 1 is the main departure */}
                                            </Typography>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => removeConnectingFlight(index)}
                                                color="error"
                                                title={t('remove_leg') || 'Remove this leg'}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        
                                        <Autocomplete
                                            size="small"
                                            options={airlineOptions}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                            value={leg.selectedAirline}
                                            onChange={(event, newValue) => {
                                                updateConnectingFlight(index, 'selectedAirline', newValue);
                                                updateConnectingFlight(index, 'airline', newValue?.code || '');
                                            }}
                                            freeSolo
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('airline_label')}
                                                    margin="dense"
                                                    size="small"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {fetchingConnectingFlight[index] ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            filterOptions={(options, { inputValue }) => filterAirlineOptions(options, inputValue)}
                                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                                        />
                                        
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField
                                                fullWidth
                                                label={t('flight_number_label')}
                                                value={leg.flightNumber}
                                                onChange={(e) => updateConnectingFlight(index, 'flightNumber', e.target.value)}
                                                margin="dense"
                                                size="small"
                                                placeholder="e.g., 1234"
                                            />
                                            <TextField
                                                fullWidth
                                                label={t('departure_date_label')}
                                                type="date"
                                                value={leg.departureDate}
                                                onChange={(e) => updateConnectingFlight(index, 'departureDate', e.target.value)}
                                                margin="dense"
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Box>
                                        
                                        <Button 
                                            onClick={() => handleFetchConnectingFlightInfo(index)} 
                                            disabled={fetchingConnectingFlight[index] || !leg.departureDate || !leg.flightNumber || !(leg.selectedAirline?.code || leg.airline)} 
                                            size="small" 
                                            variant="outlined" 
                                            sx={{ mt: 1 }}
                                        >
                                            {fetchingConnectingFlight[index] ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                            {t('fetch_flight_info_btn')}
                                        </Button>
                                        
                                        {connectingFlightNotFound[index] && (
                                            <Alert severity="warning" sx={{ mt: 1 }}>{t('flight_not_found_error')}</Alert>
                                        )}
                                        
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-end' }}>
                                            <TextField
                                                fullWidth
                                                label={t('departure_airport_label') || 'From (IATA)'}
                                                value={leg.departureAirport}
                                                onChange={(e) => updateConnectingFlight(index, 'departureAirport', e.target.value.toUpperCase())}
                                                margin="dense"
                                                size="small"
                                                inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 2 } }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder="e.g. ADD"
                                            />
                                            <Typography sx={{ mb: 0.5, color: 'text.secondary', fontSize: '1.2rem', flexShrink: 0 }}>→</Typography>
                                            <TextField
                                                fullWidth
                                                label={t('arrival_airport_label') || 'To (IATA)'}
                                                value={leg.arrivalAirport}
                                                onChange={(e) => updateConnectingFlight(index, 'arrivalAirport', e.target.value.toUpperCase())}
                                                margin="dense"
                                                size="small"
                                                inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 2 } }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder="e.g. BKK"
                                            />
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <TextField
                                                fullWidth
                                                label={t('departure_time_label')}
                                                type="time"
                                                value={leg.departureTime}
                                                onChange={(e) => updateConnectingFlight(index, 'departureTime', e.target.value)}
                                                margin="dense"
                                                size="small"
                                                disabled={!connectingFlightNotFound[index]}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                                fullWidth
                                                label={t('arrival_time_label')}
                                                type="time"
                                                value={leg.arrivalTime}
                                                onChange={(e) => updateConnectingFlight(index, 'arrivalTime', e.target.value)}
                                                margin="dense"
                                                size="small"
                                                disabled={!connectingFlightNotFound[index]}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Add Connecting Flight Button */}
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<ConnectingAirportsIcon />}
                            onClick={addConnectingFlight}
                            size="small"
                            sx={{ mb: 2, borderStyle: 'dashed' }}
                        >
                            {t('add_connecting_flight') || 'Add Connecting Flight'}
                        </Button>

                        {/* Full Route Summary */}
                        {(departureAirport || connectingFlights.length > 0) && (
                            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography variant="caption" color="info.contrastText" sx={{ fontWeight: 'bold' }}>
                                    {t('full_route') || 'Full Route'}:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    <Chip size="small" label={departureAirport || '?'} variant="outlined" />
                                    <Typography variant="body2" color="info.contrastText">→</Typography>
                                    <Chip size="small" label={arrivalAirport || '?'} variant="outlined" />
                                    {connectingFlights.map((leg, idx) => (
                                        <React.Fragment key={leg.id}>
                                            <Typography variant="body2" color="info.contrastText">→</Typography>
                                            <Chip size="small" label={leg.arrivalAirport || '?'} variant="outlined" />
                                        </React.Fragment>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {flightType === 'round-trip' && (
                            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>{t('return_flight_title')}</Typography>
                                <Autocomplete
                                    options={airlineOptions}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                    value={selectedReturnAirline}
                                    onChange={(event, newValue) => {
                                        setSelectedReturnAirline(newValue);
                                        setReturnAirline(newValue?.code || '');
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            setReturnAirline(newInputValue);
                                        }
                                    }}
                                    freeSolo
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('airline_label')}
                                            margin="normal"
                                            size="small"
                                            placeholder={t('same_as_departure_placeholder')}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {fetchingReturnFlight ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    filterOptions={(options, { inputValue }) => filterAirlineOptions(options, inputValue)}
                                    renderOption={(props, option) => (
                                        <li {...props} key={`return-${option.code}`}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="body2">{option.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.code} {option.country && `• ${option.country}`}
                                                </Typography>
                                            </Box>
                                        </li>
                                    )}
                                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                                />
                                <TextField
                                    fullWidth
                                    label={t('flight_number_label')}
                                    value={returnFlightNumber}
                                    onChange={(e) => setReturnFlightNumber(e.target.value)}
                                    margin="normal"
                                    size="small"
                                    placeholder={t('flight_number_placeholder') || 'e.g., 1234'}
                                    helperText={(selectedReturnAirline || selectedAirline) && returnFlightNumber ? `${(selectedReturnAirline?.code || selectedAirline?.code)}${returnFlightNumber}` : ''}
                                />
                                <TextField
                                    fullWidth
                                    label={t('departure_date_label')}
                                    type="date"
                                    value={returnDepartureDate}
                                    onChange={(e) => setReturnDepartureDate(e.target.value)}
                                    margin="normal"
                                    size="small"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                                <Button 
                                    onClick={() => handleFetchFlightInfo(true)} 
                                    disabled={loading || !returnDepartureDate || !returnFlightNumber || !/^\d+$/.test(returnFlightNumber) || !(selectedReturnAirline?.code || selectedAirline?.code || returnAirline)} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ mt: 1 }}
                                    title={!returnDepartureDate ? 'Date required' : (!returnFlightNumber ? 'Flight number required' : (!/^\d+$/.test(returnFlightNumber) ? 'Enter numeric flight number' : 'Airline required'))}
                                >
                                    {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                    {t('fetch_flight_info_btn')}
                                </Button>
                                {returnFlightNotFound && <Alert severity="warning" sx={{ mt: 1 }}>{t('flight_not_found_error')}</Alert>}
                                
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-end' }}>
                                    <TextField
                                        fullWidth
                                        label={t('departure_airport_label') || 'From (IATA)'}
                                        value={returnDepartureAirport}
                                        onChange={(e) => setReturnDepartureAirport(e.target.value.toUpperCase())}
                                        margin="normal"
                                        size="small"
                                        inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 2 } }}
                                        InputLabelProps={{ shrink: true }}
                                        placeholder="e.g. BKK"
                                    />
                                    <Typography sx={{ mb: 1.5, color: 'text.secondary', fontSize: '1.2rem', flexShrink: 0 }}>→</Typography>
                                    <TextField
                                        fullWidth
                                        label={t('arrival_airport_label') || 'To (IATA)'}
                                        value={returnArrivalAirport}
                                        onChange={(e) => setReturnArrivalAirport(e.target.value.toUpperCase())}
                                        margin="normal"
                                        size="small"
                                        inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 2 } }}
                                        InputLabelProps={{ shrink: true }}
                                        placeholder="e.g. ATH"
                                    />
                                    {returnFlightDataSource && (
                                        <Chip 
                                            size="small" 
                                            label={returnFlightDataSource === 'live' ? t('live_data') || 'Live' : t('historical_data') || 'Est.'}
                                            color={returnFlightDataSource === 'live' ? 'success' : 'warning'}
                                            sx={{ mb: 1, flexShrink: 0, height: 20, fontSize: '0.7rem' }}
                                        />
                                    )}
                                </Box>
                                
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <TextField
                                        fullWidth
                                        label={t('departure_time_label')}
                                        type="time"
                                        value={returnDepartureTime}
                                        onChange={(e) => setReturnDepartureTime(e.target.value)}
                                        margin="normal"
                                        size="small"
                                        disabled={!returnFlightNotFound}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                        fullWidth
                                        label={t('arrival_time_label')}
                                        type="time"
                                        value={returnArrivalTime}
                                        onChange={(e) => setReturnArrivalTime(e.target.value)}
                                        margin="normal"
                                        size="small"
                                        disabled={!returnFlightNotFound}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Return Connecting Flights Section */}
                        {flightType === 'round-trip' && returnConnectingFlights.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <ConnectingAirportsIcon color="secondary" />
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {t('return_connecting_flights_title') || 'Return Connecting Flights'}
                                    </Typography>
                                </Box>
                                {returnConnectingFlights.map((leg, index) => (
                                    <Box key={leg.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t('leg_label') || 'Leg'} {index + 2}
                                            </Typography>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => removeReturnConnectingFlight(index)}
                                                color="error"
                                                title={t('remove_leg') || 'Remove this leg'}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        
                                        <Autocomplete
                                            size="small"
                                            options={airlineOptions}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                            value={leg.selectedAirline}
                                            onChange={(event, newValue) => {
                                                updateReturnConnectingFlight(index, 'selectedAirline', newValue);
                                                updateReturnConnectingFlight(index, 'airline', newValue?.code || '');
                                            }}
                                            freeSolo
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('airline_label')}
                                                    margin="dense"
                                                    size="small"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {fetchingReturnConnectingFlight[index] ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            filterOptions={(options, { inputValue }) => filterAirlineOptions(options, inputValue)}
                                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                                        />
                                        
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField
                                                fullWidth
                                                label={t('flight_number_label')}
                                                value={leg.flightNumber}
                                                onChange={(e) => updateReturnConnectingFlight(index, 'flightNumber', e.target.value)}
                                                margin="dense"
                                                size="small"
                                                placeholder="e.g., 1234"
                                            />
                                            <TextField
                                                fullWidth
                                                label={t('departure_date_label')}
                                                type="date"
                                                value={leg.departureDate}
                                                onChange={(e) => updateReturnConnectingFlight(index, 'departureDate', e.target.value)}
                                                margin="dense"
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Box>
                                        
                                        <Button 
                                            onClick={() => handleFetchReturnConnectingFlightInfo(index)} 
                                            disabled={fetchingReturnConnectingFlight[index] || !leg.departureDate || !leg.flightNumber || !(leg.selectedAirline?.code || leg.airline)} 
                                            size="small" 
                                            variant="outlined" 
                                            sx={{ mt: 1 }}
                                        >
                                            {fetchingReturnConnectingFlight[index] ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                            {t('fetch_flight_info_btn')}
                                        </Button>
                                        
                                        {returnConnectingFlightNotFound[index] && (
                                            <Alert severity="warning" sx={{ mt: 1 }}>{t('flight_not_found_error')}</Alert>
                                        )}
                                        
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-end' }}>
                                            <TextField
                                                fullWidth
                                                label={t('departure_airport_label') || 'From (IATA)'}
                                                value={leg.departureAirport}
                                                onChange={(e) => updateReturnConnectingFlight(index, 'departureAirport', e.target.value.toUpperCase())}
                                                margin="dense"
                                                size="small"
                                                inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 2 } }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder="e.g. ADD"
                                            />
                                            <Typography sx={{ mb: 0.5, color: 'text.secondary', fontSize: '1.2rem', flexShrink: 0 }}>→</Typography>
                                            <TextField
                                                fullWidth
                                                label={t('arrival_airport_label') || 'To (IATA)'}
                                                value={leg.arrivalAirport}
                                                onChange={(e) => updateReturnConnectingFlight(index, 'arrivalAirport', e.target.value.toUpperCase())}
                                                margin="dense"
                                                size="small"
                                                inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 2 } }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder="e.g. ATH"
                                            />
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <TextField
                                                fullWidth
                                                label={t('departure_time_label')}
                                                type="time"
                                                value={leg.departureTime}
                                                onChange={(e) => updateReturnConnectingFlight(index, 'departureTime', e.target.value)}
                                                margin="dense"
                                                size="small"
                                                disabled={!returnConnectingFlightNotFound[index]}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                                fullWidth
                                                label={t('arrival_time_label')}
                                                type="time"
                                                value={leg.arrivalTime}
                                                onChange={(e) => updateReturnConnectingFlight(index, 'arrivalTime', e.target.value)}
                                                margin="dense"
                                                size="small"
                                                disabled={!returnConnectingFlightNotFound[index]}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Add Return Connecting Flight Button */}
                        {flightType === 'round-trip' && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<ConnectingAirportsIcon />}
                                onClick={addReturnConnectingFlight}
                                size="small"
                                sx={{ mb: 2, borderStyle: 'dashed' }}
                            >
                                {t('add_return_connecting_flight', 'Add Return Connecting Flight')}
                            </Button>
                        )}

                        {/* Full Return Route Summary */}
                        {flightType === 'round-trip' && (returnDepartureAirport || returnConnectingFlights.length > 0) && (
                            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                                <Typography variant="caption" color="warning.contrastText" sx={{ fontWeight: 'bold' }}>
                                    {t('full_return_route') || 'Full Return Route'}:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    <Chip size="small" label={returnDepartureAirport || '?'} variant="outlined" />
                                    <Typography variant="body2" color="warning.contrastText">→</Typography>
                                    <Chip size="small" label={returnArrivalAirport || '?'} variant="outlined" />
                                    {returnConnectingFlights.map((leg, idx) => (
                                        <React.Fragment key={leg.id}>
                                            <Typography variant="body2" color="warning.contrastText">→</Typography>
                                            <Chip size="small" label={leg.arrivalAirport || '?'} variant="outlined" />
                                        </React.Fragment>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <TextField
                            fullWidth
                            label={t('free_text_label')}
                            value={freeText}
                            onChange={(e) => setFreeText(e.target.value)}
                            margin="normal"
                            multiline
                            rows={2}
                        />
                        <TextField
                            fullWidth
                            label={t('destination_city_label')}
                            value={destinationCity}
                            onChange={(e) => setDestinationCity(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label={t('booking_reference_number_label')}
                            value={bookingReference}
                            onChange={(e) => setBookingReference(e.target.value)}
                            margin="normal"
                        />
                    </>
                )}

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{ mt: 2 }}
                >
                    {loading ? t('saving_btn') : t('save_transportation_btn')}
                </Button>
            </Box>
        </Modal>
        
        {/* Flight Selection Dialog */}
        <Dialog 
            open={flightSearchDialogOpen} 
            onClose={() => setFlightSearchDialogOpen(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {t('select_flight_title') || 'Select Flight'}
                <IconButton
                    aria-label="close"
                    onClick={() => setFlightSearchDialogOpen(false)}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <List>
                    {flightSearchResults.map((flight, index) => {
                        const depTime = flight.departure?.at?.includes('T') 
                            ? flight.departure.at.split('T')[1].substring(0, 5) 
                            : flight.departure?.at || '';
                        const arrTime = flight.arrival?.at?.includes('T') 
                            ? flight.arrival.at.split('T')[1].substring(0, 5) 
                            : flight.arrival?.at || '';
                        const depAirport = flight.departure?.iataCode || '';
                        const arrAirport = flight.arrival?.iataCode || '';
                        const flightCode = flight.flightDesignator 
                            ? `${flight.flightDesignator.carrierCode}${flight.flightDesignator.flightNumber}`
                            : '';
                        
                        return (
                            <ListItem key={index} disablePadding>
                                <ListItemButton onClick={() => handleSelectFlightExtended(flight)}>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {flightCode}
                                                </Typography>
                                                <Chip 
                                                    size="small" 
                                                    label={`${depAirport} → ${arrAirport}`}
                                                    color="primary"
                                                    icon={<FlightTakeoffIcon />}
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="body2" color="text.secondary">
                                                {t('departure_label') || 'Departure'}: {depTime} | {t('arrival_label') || 'Arrival'}: {arrTime}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setFlightSearchDialogOpen(false)}>
                    {t('cancel_btn') || 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    </>
    );
};

export default TransportationFormModal;
